import { useSelector } from "react-redux";
import {
  marketplaceFilters,
  signalsBotBacktestingFilters,
} from "@shared/slices/filters.slice";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { selectMarketplaceLoadingStates } from "@shared/slices/marketplace.slice";
import SortSelect from "common/Select/SortSelect";
import { GroupsSort } from "@shared/features/newMarketplace/types";
import LayoutSwitch from "common/Switch/LayoutSwitch";
import FilterOutlined from "@ant-design/icons/FilterOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import TableOutlined from "@ant-design/icons/TableOutlined";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Button from "common/Buttons/Button";
import FiltersCounter from "common/Filters/common/FiltersCounter";
import React, { ReactNode, useMemo, useState } from "react";
import { SortingOrder } from "types";
import useMedia from "utils/hooks/useMedia";
import { SINGLE_FILTERS_ROW_BREAKPOINT } from "@shared/features/filters/constants";
import styled, { css, useTheme } from "styled-components";
import { useBottomNavigator } from "utils/hooks/misc";
import Switch from "common/Buttons/Switch.tsx";
import Text from "common/Typography/Text";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { LayoutGrid, List } from "lucide-react";
import { SectionHeader, usePageIntro } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import NewLookFiltersBar from "features/newMarketplace/NewLook/NewLookFiltersBar";
import {
  NewLookChipsRow,
  newLookFilterPill,
} from "features/newMarketplace/NewLook/filterPillStyles.ts";

interface GroupsWrapperProps {
  sortBy: GroupsSort;
  setSortBy: (sortBy: GroupsSort) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
  isCardsView: boolean;
  setIsCardsView: (isCardsView: boolean) => void;
  SearchInput?: ReactNode;
  useMobileSearch?: boolean;
  showFullList?: boolean;
  setShowFullList?: (showFullList: boolean) => void;
  showFullListToggle?: boolean;
  /** New look: number of groups shown, for the section header count. */
  numGroups?: number;
  /** New look (desktop): the list the header and filters act on. */
  children?: ReactNode;
  /** New look (desktop): the list sits inside the groups panel (tables). */
  asPanel?: boolean;
  /** New look (desktop): the list failed to load, so there is nothing to filter. */
  hideFilters?: boolean;
}

const GroupsWrapper = ({
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  isCardsView,
  setIsCardsView,
  SearchInput,
  useMobileSearch,
  showFullList,
  setShowFullList,
  showFullListToggle,
  numGroups,
  children,
  asPanel,
  hideFilters,
}: GroupsWrapperProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { isNewLook } = useTheme();
  const isSmallScreen = useMedia([`(max-width: 1300px)`], [true], false);
  const isSingleFiltersChipsRow = useMedia(
    [`(max-width: ${SINGLE_FILTERS_ROW_BREAKPOINT})`],
    [true],
    false
  );
  const [showFilters, setShowFilters] = useState(false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const numActiveFilters = useSelector(
    signalsBotBacktestingFilters.selectors.selectNumActiveFilters
  );

  const loadingStatesSelector = useMemo(
    () => selectMarketplaceLoadingStates(showFullList),
    [showFullList]
  );
  const { loading } = useSelector(loadingStatesSelector);

  usePageWrapper({
    filters: {
      loadingStatesSelector,
      actions: marketplaceFilters.actions,
      selectors: marketplaceFilters.selectors,
    },
    showSearch: true,
  });

  const sortSelect = (
    <SortSelect
      setSortBy={setSortBy}
      sortBy={sortBy}
      sortDirection={sortDirection}
      setSortDirection={setSortDirection}
      sortValues={Object.values(GroupsSort)}
      rounded={true}
      isSingleFiltersChipsRow={isSingleFiltersChipsRow}
      labelMapper={(value) => t(value)}
    />
  );

  const layoutSwitch = !isBottomNavigator && (
    <LayoutSwitch
      layoutIcons={
        isNewLook
          ? [
              <List size={16} strokeWidth={1.75} />,
              <LayoutGrid size={16} strokeWidth={1.75} />,
            ]
          : [<MenuOutlined />, <TableOutlined />]
      }
      selectedLayoutIndex={isCardsView ? 1 : 0}
      onClick={() => setIsCardsView(!isCardsView)}
    />
  );

  const fullListToggle = showFullListToggle && (
    <SwitchWrapper
      $isSingleRowView={isSingleFiltersChipsRow}
      onClick={() => setShowFullList?.(!showFullList)}
    >
      <Text style={{ marginRight: 6 }}>{t("filters.full_list")}</Text>
      <Switch
        checked={showFullList}
        size={isSingleFiltersChipsRow || isNewLook ? "small" : "default"}
        loading={loading}
      />
    </SwitchWrapper>
  );

  const getExpandableChipsContent = () => {
    const filtersChips = (
      <FiltersChips
        filtersActions={marketplaceFilters.actions}
        filtersSelectors={marketplaceFilters.selectors}
        extraComponent={
          <>
            {fullListToggle}
            {sortSelect}
            {layoutSwitch}
          </>
        }
      />
    );
    if (isBottomNavigator) return filtersChips;
    return (
      <ExpandCollapse expanded={showFilters || isSingleFiltersChipsRow}>
        <div style={{ paddingTop: isSingleFiltersChipsRow ? 0 : 16 }}>
          {filtersChips}
        </div>
      </ExpandCollapse>
    );
  };

  const actionsRow = (
    <Flex $justifyContent={isSmallScreen ? "flex-end" : "space-between"}>
      {!isSmallScreen && (
        <Title level={3} type="secondary">
          {t("groups_title")}
        </Title>
      )}
      <ActionsWrapper>
        {fullListToggle}
        {sortSelect}
        {!useMobileSearch && SearchInput}
        {isSmallScreen && layoutSwitch}
        <FiltersButtonWrapper>
          <Button onClick={() => setShowFilters(!showFilters)}>
            <FilterOutlined />
            {t("filters.filters")}
          </Button>
          <FiltersCounter numActiveFilters={numActiveFilters} />
        </FiltersButtonWrapper>
      </ActionsWrapper>
    </Flex>
  );

  const isNewLookDesktop = isNewLook && !isBottomNavigator;

  // New look: the page keeps no toolbar; search, sorting and the filters all
  // sit with the list they act on.
  usePageIntro({}, []);

  if (isNewLookDesktop) {
    const actions = (
      <NewLookActions>
        {!useMobileSearch && SearchInput}
        {!isSingleFiltersChipsRow && (
          <>
            {fullListToggle}
            {sortSelect}
            {isSmallScreen && layoutSwitch}
          </>
        )}
      </NewLookActions>
    );
    const filtersBar = hideFilters ? null : loading && !numGroups ? (
      <FiltersSkeleton aria-hidden={true}>
        {[120, 88, 120, 104].map((width, index) => (
          <Skeleton key={index} width={width} height={32} radius={16} />
        ))}
      </FiltersSkeleton>
    ) : (
      <NewLookFiltersBar
        hideFiltersButton={!isSingleFiltersChipsRow}
        extraComponent={
          isSingleFiltersChipsRow && (
            <>
              {fullListToggle}
              {sortSelect}
              {layoutSwitch}
            </>
          )
        }
      />
    );
    if (asPanel) {
      return (
        <Panel
          title={t("groups_title")}
          count={numGroups}
          actions={actions}
          flush={true}
        >
          {filtersBar && <NewLookToolbar>{filtersBar}</NewLookToolbar>}
          {children}
        </Panel>
      );
    }
    return (
      <NewLookWrapper>
        <SectionHeader
          title={t("groups_title")}
          count={numGroups}
          actions={actions}
        />
        {filtersBar}
        {children}
      </NewLookWrapper>
    );
  }

  if (isNewLook) {
    return (
      <NewLookChipsRow style={{ width: "100%" }}>
        {getExpandableChipsContent()}
      </NewLookChipsRow>
    );
  }

  return (
    <div
      style={{
        paddingBottom: isBottomNavigator ? 0 : 16,
        width: isBottomNavigator ? "100%" : undefined,
      }}
    >
      {!isSingleFiltersChipsRow && actionsRow}
      {getExpandableChipsContent()}
    </div>
  );
};

export default GroupsWrapper;

const NewLookWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  > :first-child {
    margin-bottom: 0;
  }
  > :last-child {
    margin-top: 4px;
  }
`;
const FiltersSkeleton = styled.div`
  display: flex;
  gap: 8px;
`;
const NewLookToolbar = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const NewLookActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const FiltersButtonWrapper = styled.div`
  position: relative;
`;
const ActionsWrapper = styled(Flex)`
  column-gap: 8px;
`;
const SwitchWrapper = styled.div<{
  $isSingleRowView?: boolean;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  ${(p) =>
    p.$isSingleRowView &&
    `
     background: ${p.theme.colors.filterChipsColor};
     border: none;
     border-radius: 50px;
     padding: 0 10px;
     white-space: nowrap;
  `}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      flex: none;
      gap: 2px;
      padding: 0 8px 0 14px !important;
      white-space: nowrap;
      ${newLookFilterPill};
      p {
        color: inherit;
        font-size: 13px;
      }
    `}
`;
